import React, {useState, useEffect} from 'react';
import t from 'babel-plugin-yate/macro';
import {useSelector} from 'react-redux';
import {Form, Field} from 'react-final-form';

import {
  composeFormValidators,
  hasMinVal,
  hasMaxVal,
  isRequired,
  isIBAN,
  isNumberWithTwoDecimals,
} from '../lib/validations';
import {formatNonWhiteSpaceFormField, parseNumberFormField} from '../lib/misc';

import Condition from '../components/Condition';
import {Input} from '../components/Form/Input';
import {Select} from '../components/Form/Select';
import TransactionEmail from './TransactionEmail';
import Label from '../components/Form/Label';
import RadioButton from '../components/Form/RadioButton';
import {DocumentsUploadBox} from '../components/DocumentsUploadBox';
import {Block} from '../components/uiElem';

function ReceivedDetailsForm(props) {
  const {handleAction, initialValues, formRef, disabled, page} = props;
  const {configInstallments} = useSelector(({transaction}) => transaction);
  const [isEmailValidated, setIsEmailValidated] = useState(false);

  useEffect(() => {
    isEmailValidated && handleAction({}, true);
  }, [isEmailValidated, handleAction]);

  return (
    <Block
      heading={t`Current personal details`}
      description={
        page === 'Step1'
          ? t`Fill in and review your personal data to go to the next step`
          : undefined
      }
      descriptionClassName="jupiter">
      <Form
        keepDirtyOnReinitialize={true}
        onSubmit={handleAction}
        initialValues={initialValues}
        validate={values => {
          if (disabled) {
            return;
          }

          const customErrors = {
            email_validation_warn: undefined,
            bank_statement: undefined,
          };

          if (!isEmailValidated) {
            customErrors.email_validation_warn = t`The email must be validated!`;
          }

          if (values.payment === 'iban' && !values.bank_statement) {
            customErrors.bank_statement = true;
          }

          return customErrors;
        }}
        render={({
          handleSubmit,
          values,
          submitFailed,
          submitSucceeded,
          form,
        }) => {
          return (
            <form onSubmit={handleSubmit} ref={formRef}>
              <div className="pt-8 p-2">
                <div className="md:flex md:flex-wrap">
                  <div className="w-full md:w-1/2 px-2 mb-4">
                    <Field
                      name="cnp"
                      label={t`Social Security no`}
                      component={Input}
                      disabled={true}
                      type="number"
                    />
                  </div>
                </div>

                <div className="md:flex md:flex-wrap">
                  <div className="w-full md:w-1/2 px-2 mb-4">
                    <Field
                      name="last_name"
                      label={t`Last name`}
                      component={Input}
                      disabled={true}
                    />
                  </div>

                  <div className="w-full md:w-1/2 px-2 mb-4">
                    <Field
                      name="first_name"
                      label={t`First name`}
                      component={Input}
                      disabled={true}
                    />
                  </div>
                </div>

                <div className="md:flex md:flex-wrap">
                  <div className="w-full md:w-1/2 px-2 mb-4">
                    <Field
                      name="mobile_phone"
                      label={t`Mobile number`}
                      component={Input}
                      disabled={true}
                    />
                  </div>

                  <div className="w-full md:w-1/2 px-2 mb-4">
                    <TransactionEmail
                      transaction={values}
                      disableValidationBtn={disabled}
                      handlerSetIsEmailValidated={setIsEmailValidated}
                    />
                  </div>
                </div>

                {/* txpnl */}
                {values.tx_type === 5 && (
                  <div className="md:flex md:flex-wrap">
                    <div className="w-full md:w-1/2 px-2 mb-4">
                      <Field
                        name="balance"
                        label={t`Suma disponibila`}
                        disabled={true}
                        component={Input}
                        helper={
                          !values.balance &&
                          'Valideaza email pentru a verifica limita de credit disponibila.'
                        }
                      />
                    </div>
                  </div>
                )}

                <div className="md:flex md:flex-wrap">
                  <div className="w-full md:w-1/2 px-2 mb-4">
                    <Field
                      name="installments"
                      label={t`Duration in months`}
                      component={Select}
                      validate={isRequired()}
                      parse={parseNumberFormField}
                      disabled={!isEmailValidated || disabled}
                      loadingData={!configInstallments}>
                      <option value="">{t`Choose`}</option>
                      {configInstallments?.map(({id, name, code, display}) => (
                        <option
                          key={`installments-${id}`}
                          value={code}
                          hidden={!display}>
                          {name}
                        </option>
                      ))}
                    </Field>
                  </div>

                  {/* txpnl */}
                  {values.tx_type === 5 && (
                    <div className="w-full md:w-1/2 px-2 mb-4">
                      <Field
                        name="requested_amount"
                        label={t`Suma solicitata`}
                        disabled={!isEmailValidated || disabled}
                        component={Input}
                        type="number"
                        parse={parseNumberFormField}
                        validate={composeFormValidators(
                          isRequired(),
                          isNumberWithTwoDecimals(),
                          hasMinVal(1000),
                          hasMaxVal(
                            values.balance ?? values.requested_amount,
                            'Suma solicitata depaseste limita de credit disponibila. Modificati suma.',
                          ),
                        )}
                      />
                    </div>
                  )}
                </div>

                {/* txpnl */}
                {values.tx_type === 5 && (
                  <>
                    <div className="md:flex md:flex-wrap">
                      <div className="w-full md:w-1/2 px-2 mb-4">
                        <Label value={t`Alege metoda de incasare`} />
                        <div className="mt-4">
                          <Field
                            name="payment"
                            component={RadioButton}
                            type="radio"
                            value="card"
                            disabled={!isEmailValidated || disabled}
                            label={t`Payment with credit card`}
                            validate={composeFormValidators(isRequired())}
                            helper={
                              page === 'Step1' &&
                              `In functie de procedurile bancii emitente a cardului, efectuarea tranzactiei poate dura mai mult. Sunt permise doar carduri emise de banci romanesti.`
                            }
                          />
                        </div>
                        <div className="mt-4">
                          <Field
                            name="payment"
                            component={RadioButton}
                            type="radio"
                            value="iban"
                            disabled={!isEmailValidated || disabled}
                            label={t`Payment with IBAN`}
                            validate={composeFormValidators(isRequired())}
                            helper={
                              page === 'Step1' && `Plata in maxim 3 zile.`
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <Condition when="payment" is="iban">
                      <Field
                        name="bank_account"
                        label={t`Bank account`}
                        component={Input}
                        format={formatNonWhiteSpaceFormField}
                        disabled={!isEmailValidated || disabled}
                        validate={composeFormValidators(isRequired(), isIBAN())}
                      />
                    </Condition>
                  </>
                )}

                {page === 'Step1' && (
                  <div className="md:flex md:flex-wrap">
                    <div className="w-full md:w-1/2 px-2 mb-4">
                      <Condition when="payment" is="iban">
                        <Block
                          heading={t`Upload proof of bank statement`}
                          secondary>
                          <DocumentsUploadBox
                            acceptFileTypes="image/*,.pdf"
                            description={t`Accepted file types: ${'.pdf, .jpg, .jpeg, .png'}.`}
                            isFileUploaded={values.bank_statement}
                            isFileMissing={
                              (submitFailed || submitSucceeded) &&
                              !values.bank_statement
                            }
                            isLoading={false}
                            onUpload={file => {
                              form.change('bank_statement', {
                                file_name: file.name,
                                file_content: file.base64,
                                type: 'bank_statement',
                              });
                            }}
                            uploadInputLabel={t`Upload document`}
                            documents={values.bank_statement}
                            showDownloadLink={false}
                          />
                        </Block>
                      </Condition>
                    </div>
                  </div>
                )}
              </div>
            </form>
          );
        }}
      />
    </Block>
  );
}

export default ReceivedDetailsForm;
